import { useState, useEffect, type ReactNode } from 'react';
import { produce } from 'immer';

import {
  generateToken,
  loadRecaptcha,
  CaptchaDisclosure,
} from '@hungryroot/captcha';
import { useSignup } from '@hungryroot/customer';
import { useThirdPartySSODisabledUserAgentMatch } from '@hungryroot/environment';
import { useWebLoginChallenge } from '@hungryroot/feature-flags';

import { Button, Heading, TextInput } from '@hungryroot/ramen';
import { type SignupResponse } from '@hungryroot/rest/api/signup/post';

import styles from './SignUpForm.module.css';

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

type Props = {
  submitButtonText?: string;
  onSignupSuccess: (data: SignupResponse) => void;
  ssoButtons: ReactNode;
  heading?: ReactNode;
  className?: string;
};

const EMAIL_ALREADY_TAKEN_ERROR =
  "There's already an account with this email address.";
const BAD_PASSWORD_ERROR_TOO_COMMON =
  'Avoid using commonly used passwords (ex: Password)';
const BAD_PASSWORD_ERROR_TOO_SHORT = 'Must be at least 8 characters.';

export function SignUpForm({
  submitButtonText = 'Sign up',
  heading = (
    <Heading className={styles['continue-with']} variant="H6">
      Sign up with
    </Heading>
  ),
  onSignupSuccess,
  className,
  ssoButtons,
}: Props) {
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const signup = useSignup();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const isThirdPartySSODisabledUserAgentMatched =
    useThirdPartySSODisabledUserAgentMatch();

  const captcha = useWebLoginChallenge({
    recaptcha: {
      load: async () => {
        loadRecaptcha();
      },
      generateToken: async () => {
        return await generateToken({ action: 'signup' });
      },
      captchaMessage: <CaptchaDisclosure />,
    },
    featureFlag: 'web.signup_challenge',
  });

  useEffect(() => {
    if (captcha === undefined || captcha === null) {
      return;
    }

    captcha.load().then(() => {
      setCaptchaLoaded(true);
    });
  }, [captcha]);

  const captchaReady = captchaLoaded === true || captcha === null;
  if (captcha === undefined || captchaReady === false) {
    return;
  }

  const { firstName, lastName, email, password } = formData;

  return (
    <div className={styles['flex-wrapper']}>
      <div className={styles.container}>
        {heading}
        {!isThirdPartySSODisabledUserAgentMatched && (
          <div className={styles['social-buttons']}>{ssoButtons}</div>
        )}
        <Heading variant="H6" className={styles.or}>
          or
        </Heading>
        <form
          className={`${styles.fieldset} ${className}`}
          onSubmit={async (e) => {
            e.preventDefault();
            setIsSubmitting(true);

            signup.mutate(
              { captcha_token: await captcha?.generateToken(), ...formData },
              {
                onSuccess: (data) => {
                  onSignupSuccess(data);
                },
                onError(error) {
                  switch (error.type) {
                    case 'user-already-exists': {
                      setErrors({
                        ...errors,
                        email: EMAIL_ALREADY_TAKEN_ERROR,
                      });
                      return;
                    }
                    case 'invalid-password': {
                      const errorMessage = error.errors.map((type) => {
                        if (type === 'too-common') {
                          return BAD_PASSWORD_ERROR_TOO_COMMON;
                        }

                        return BAD_PASSWORD_ERROR_TOO_SHORT;
                      });

                      setErrors({
                        ...errors,
                        password: errorMessage.join(' '),
                      });
                    }
                  }
                },
                onSettled() {
                  setIsSubmitting(false);
                },
              }
            );
          }}
        >
          <div className={styles.row}>
            <TextInput
              validateOnBlur={(value) => {
                if (errors.firstName === undefined) {
                  return;
                }
                if (value !== '') {
                  setErrors(
                    produce((draft) => {
                      delete draft.firstName;
                    })
                  );
                }
              }}
              errorMessage={errors.firstName}
              onInvalid={() => {
                setErrors({
                  ...errors,
                  firstName: 'Enter first name',
                });
              }}
              value={firstName}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  firstName: value,
                });
              }}
              label="First name"
              validationBehavior="native"
              isRequired
              className={styles['input-name']}
            />
            <TextInput
              value={lastName}
              errorMessage={errors.lastName}
              validateOnBlur={(value) => {
                if (errors.lastName === undefined) {
                  return;
                }
                if (value !== '') {
                  setErrors(
                    produce((draft) => {
                      delete draft.lastName;
                    })
                  );
                }
              }}
              onInvalid={() => {
                setErrors({
                  ...errors,
                  lastName: 'Enter last name',
                });
              }}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  lastName: value,
                });
              }}
              label="Last name"
              validationBehavior="native"
              isRequired
              className={styles['input-name']}
            />
          </div>
          <div className={styles.row}>
            <TextInput
              validateOnBlur={(value) => {
                if (errors.email === undefined) {
                  return;
                }
                if (value !== '') {
                  setErrors(
                    produce((draft) => {
                      delete draft.email;
                    })
                  );
                }
              }}
              onInvalid={() => {
                setErrors({
                  ...errors,
                  email: 'Enter valid email',
                });
              }}
              errorMessage={errors.email}
              value={email}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  email: value,
                });
              }}
              label="Email"
              validationBehavior="native"
              isRequired
              className={styles.input}
              type="email"
            />
          </div>
          <div className={styles['password-row']}>
            <TextInput
              errorMessage={errors.password}
              onInvalid={() => {
                setErrors({
                  ...errors,
                  password: 'Enter password',
                });
              }}
              value={password}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  password: value,
                });
              }}
              label="Password"
              validationBehavior="native"
              isRequired
              className={styles.input}
              type="password"
              helpText="Must be at least 8 characters"
            />
          </div>

          <div className={styles.captcha}>{captcha?.captchaMessage}</div>
          <Button
            style="default"
            width="full-width"
            size="medium"
            prominence="primary"
            textPrimary={submitButtonText}
            buttonType="submit"
            disabled={isSubmitting}
          />
        </form>
      </div>
    </div>
  );
}
